import { Block, IconButton, SplitLayout } from '@aloft/toolkit-sui';

import { ReactNode, useEffect, useState } from 'react';

import { useResizablePanelRef } from '../hooks/useResizablePanelRef.ts';
import { DRAWER_HEADER_HEIGHT } from './constants.ts';

/** basic drawer management is done internally
 * pass in your own drawer ref using {@link useResizablePanelRef} to enable custom behavior
 * like getting and setting height, collapsing, and expanding
 */
export const ResizableDrawerLayout = ({
  isOpen,
  children,
  drawerContent,
  onClose,
  drawerRef,
  canClose = true,
  direction = 'vertical',
  defaultSize = 50,
  isResizable,
}: {
  isOpen: boolean;
  onClose: VoidFunction;
  canClose?: boolean;
  children: ReactNode;
  drawerContent: ReactNode;
  drawerRef?: ReturnType<typeof useResizablePanelRef>;
  direction?: 'vertical' | 'horizontal';
  defaultSize?: number;
  isResizable: boolean;
}) => {
  const [panelBSize, setPanelBSize] = useState<number>(defaultSize);
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const panelBRef = useResizablePanelRef();
  const panelBRefFinal = drawerRef ?? panelBRef;

  useEffect(() => {
    if (isMounted) {
      if (isOpen) {
        panelBRefFinal.current?.resize(panelBSize);
      } else {
        const currentSize = panelBRefFinal.current?.getSize() ?? 0;
        if (currentSize !== 0) {
          setPanelBSize(currentSize);
        }
        panelBRefFinal.current?.collapse();
      }
    }
  }, [isMounted, isOpen, panelBRefFinal, panelBSize]);

  useEffect(() => setIsMounted(true), []);

  return (
    <SplitLayout
      direction={direction}
      panelA={children}
      panelB={
        <Block
          position="relative"
          height="100%"
          backgroundColor="surface2"
          borderStyle="solid"
          borderTopWidth="0.75px"
          borderTopColor="surface4"
        >
          {canClose && (
            <Block
              backgroundColor="transparent"
              position="absolute"
              right={0}
              top="0.5rem"
              zIndex={3}
              // FIXME: Kind of hacky way to get everything to align properly
              height={DRAWER_HEADER_HEIGHT}
              marginRight={2}
            >
              <IconButton
                onClick={() => {
                  panelBRefFinal.current?.collapse();
                  onClose();
                }}
                icon="close"
                iconSize="lg"
                size="sm"
                variant="tertiary"
                ariaLabel="close"
                iconColor="textTertiary"
              />
            </Block>
          )}
          <Block height="100%" width="100%" overflowY="auto">
            {drawerContent}
          </Block>
        </Block>
      }
      config={{
        isResizable: isResizable && isOpen,
        panelAProps: { collapsible: true },
        panelBProps: {
          collapsible: true,
          isCollapsed: !isOpen,
        },
        panelBRef: panelBRefFinal,
      }}
    />
  );
};
